import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Error as ErrorIcon,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { ProcessIncident } from '../types/process';

interface ActivityIncidentsProps {
  activityId: string;
  incidents: ProcessIncident[];
}

export const ActivityIncidents: React.FC<ActivityIncidentsProps> = ({
  activityId,
  incidents,
}) => {
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getIncidentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'failedJob': 'Falha no Job',
      'failedExternalTask': 'Falha em Tarefa Externa',
      'failedProcessStart': 'Falha ao Iniciar Processo',
      'unhandledBpmnError': 'Erro BPMN Não Tratado',
    };
    return types[type] || type;
  };

  // Filtrar incidentes relacionados a esta atividade
  const activityIncidents = incidents.filter(
    incident => 
      incident.activity_id === activityId || 
      incident.failed_activity_id === activityId
  );

  if (activityIncidents.length === 0) {
    return (
      <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
        Esta atividade não possui incidentes registrados.
      </Alert>
    );
  }

  const openIncidents = activityIncidents.filter(i => i.open);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ErrorIcon sx={{ color: openIncidents.length > 0 ? 'error.main' : 'text.secondary', fontSize: 24 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Incidentes desta Atividade ({activityIncidents.length})
        </Typography>
      </Box>

      {openIncidents.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{openIncidents.length}</strong> incidente(s) aberto(s) nesta atividade.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {activityIncidents.map((incident) => (
          <Paper
            key={incident.id}
            elevation={incident.open ? 3 : 1}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: incident.open ? 'error.main' : 'divider',
              backgroundColor: incident.open ? 'error.light' : 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {incident.resolved ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
              ) : incident.open ? (
                <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
              ) : (
                <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
              )}
              <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
                {getIncidentTypeLabel(incident.incident_type || '')}
              </Typography>
              {incident.resolved && (
                <Chip label="Resolvido" color="success" size="small" />
              )}
              {incident.open && (
                <Chip label="Aberto" color="error" size="small" />
              )}
            </Box>

            {incident.incident_message && (
              <Alert severity={incident.open ? 'error' : 'info'} sx={{ mb: 1, mt: 1 }}>
                <Typography variant="body2">{incident.incident_message}</Typography>
              </Alert>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Criado em
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                  {formatDate(incident.create_time)}
                </Typography>
              </Box>
              {incident.end_time && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Resolvido em
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                    {formatDate(incident.end_time)}
                  </Typography>
                </Box>
              )}
              {incident.failed_activity_id && incident.failed_activity_id !== activityId && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Atividade que falhou
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                    {incident.failed_activity_id}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

